import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { formatIDR, formatAUD, formatDate } from "@/lib/format";
import { PageHeader, Loading, EmptyState, Field, ImageUpload, SkeletonCardGrid } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { CategoryCombobox } from "@/components/CategoryCombobox";
import { SearchableSelect } from "@/components/SearchableSelect";
import PricingCalculator from "@/components/PricingCalculator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Search, Pencil, Trash2, LayoutGrid, List, ChevronDown, Link2, Loader2, Camera, Scale, X, Tag, Percent, Archive, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import { ScreenshotImportDialog } from "@/components/ScreenshotImportDialog";
import { ProductPlaceholder } from "@/components/Logo";
import { LabelBadges } from "@/components/LabelBadges";

const EMPTY = {
  trip_id: "", name: "", brand: "", category: "", store_name: "", store_location: "",
  product_url: "", description: "", variant: "", colour: "", size: "", weight: "", estimated_weight: "",
  aud_price: "", aud_sale_price: "", notes: "", availability: "Tersedia", labels: [],
  discount_active: false, discount_type: "percent", discount_percent: "", discount_amount: "",
  photo: null, photos: [], margin_percent: "", jasa_titip_type: "", jasa_titip_value: "",
  delivery_fee: "", additional_fee: "0", discount: "0", rounding_method: "", custom_step: 0,
};

export default function Products() {
  const { constants, trips, selectedTripId } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [search, setSearch] = useState("");
  const [availFilter, setAvailFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [facets, setFacets] = useState({ brands: [], categories: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [delTarget, setDelTarget] = useState(null);
  const [qtyFilter, setQtyFilter] = useState("");
  const [summary, setSummary] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailTab, setDetailTab] = useState("info");
  const [categories, setCategories] = useState([]);
  const [advOpen, setAdvOpen] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkOp, setBulkOp] = useState("discount_percent");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkArchive, setBulkArchive] = useState(false);

  const toggleSelect = (id) => setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const allSelected = items.length > 0 && selectedIds.length === items.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : items.map((p) => p.id));
  const clearSelection = () => setSelectedIds([]);
  const clearFilters = () => { setSearch(""); setAvailFilter(""); setCatFilter(""); setBrandFilter(""); setQtyFilter(""); };
  const hasFilters = search || availFilter || catFilter || brandFilter || qtyFilter;

  const runBulk = async (operation, value) => {
    if (!selectedIds.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/products/bulk-update", { ids: selectedIds, operation, value });
      toast.success(`${data.modified} produk diperbarui`);
      setBulkOpen(false); setBulkArchive(false); setBulkValue(""); clearSelection();
      load(); loadFacets();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memperbarui produk");
    } finally { setBulkBusy(false); }
  };

  const applyBulkEdit = () => {
    if (["discount_percent", "discount_amount", "add_label", "remove_label", "category", "availability"].includes(bulkOp)) {
      if (bulkValue === "" || bulkValue == null) return toast.error("Isi nilai dulu");
    }
    runBulk(bulkOp, bulkValue);
  };
  const [importMissing, setImportMissing] = useState([]);
  const [screenshotOpen, setScreenshotOpen] = useState(false);
  const [weighing, setWeighing] = useState(false);

  const estimateWeight = async () => {
    if (!form.name) return toast.error("Isi nama produk dulu");
    setWeighing(true);
    try {
      const { data } = await api.post("/ai/estimate-weight", {
        name: form.name, brand: form.brand, description: form.description, size: form.size, variant: form.variant,
      });
      set("estimated_weight", data.estimated_weight);
      toast.success(`Estimasi berat: ${data.weight_kg} kg${data.note ? ` (${data.note})` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengestimasi berat");
    } finally { setWeighing(false); }
  };

  const doImport = async () => {
    const url = importUrl.trim();
    if (!url) return toast.error("Tempel link produk dulu");
    setImporting(true);
    try {
      const { data } = await api.post("/products/import-from-url", { url });
      setForm((f) => ({ ...f,
        name: data.name || f.name,
        brand: data.brand || f.brand,
        description: data.description || f.description,
        product_url: data.product_url || url,
        aud_price: data.price_aud != null ? String(data.price_aud) : f.aud_price,
        photo: data.photo || data.image_url || f.photo,
      }));
      setImportMissing(data.missing || []);
      if (data.brand || data.product_url) setAdvOpen(true);
      (data.warnings || []).forEach((w) => toast.message(w));
      if ((data.missing || []).length) toast.warning("Lengkapi manual: " + data.missing.join(", "));
      else toast.success("Data produk terisi dari link");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal impor dari link");
    } finally { setImporting(false); }
  };


  const loadCategories = useCallback(async () => {
    try { const r = await api.get("/categories"); setCategories(r.data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);
  const loadFacets = useCallback(async () => {
    try { const r = await api.get("/product-facets"); setFacets(r.data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadFacets(); }, [loadFacets]);

  const createCategory = async (name) => {
    try {
      await api.post("/categories", { name });
      await loadCategories();
      toast.success(`Kategori "${name}" dibuat`);
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (e.response?.status === 403 || detail.includes("sudah ada")) return true;
      toast.error(detail || "Gagal membuat kategori");
      return false;
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/products", { params: { trip_id: selectedTripId, search: search || undefined, availability: availFilter || undefined, category: catFilter || undefined, brand: brandFilter || undefined, quantity_status: qtyFilter || undefined } }),
        api.get("/products-quantity-summary", { params: { trip_id: selectedTripId } }),
      ]);
      setItems(r.data.items); setSummary(s.data);
    } finally { setLoading(false); }
  }, [selectedTripId, search, availFilter, catFilter, brandFilter, qtyFilter]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openDetail = async (p) => { setDetailTab("info"); const r = await api.get(`/products/${p.id}/detail`); setDetail(r.data); };

  const currentTrip = trips.find((t) => t.id === (form.trip_id || selectedTripId)) || trips[0];
  const rate = currentTrip?.exchange_rate || "11000";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // When trip changes in the form, sync pricing defaults to the newly selected trip
  const onTripChange = (tid) => {
    const t = trips.find((x) => x.id === tid);
    setForm((f) => ({ ...f, trip_id: tid,
      margin_percent: t?.default_margin_percent || "",
      jasa_titip_type: t?.default_jasa_titip_type || "fixed",
      jasa_titip_value: t?.default_jasa_titip_value || "",
      delivery_fee: t?.default_delivery_fee || "",
      rounding_method: t?.default_rounding_method || "none" }));
  };

  const openNew = () => {
    const tid = selectedTripId !== "all" ? selectedTripId : (trips[0]?.id || "");
    const t = trips.find((x) => x.id === tid);
    setEditing(null);
    setForm({ ...EMPTY, trip_id: tid,
      margin_percent: t?.default_margin_percent || "", jasa_titip_type: t?.default_jasa_titip_type || "fixed",
      jasa_titip_value: t?.default_jasa_titip_value || "", delivery_fee: t?.default_delivery_fee || "",
      rounding_method: t?.default_rounding_method || "none" });
    setAdvOpen(false);
    setImportUrl(""); setImportMissing([]);
    setOpen(true);
  };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setAdvOpen(false); setImportUrl(""); setImportMissing([]); setOpen(true); };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") { openNew(); searchParams.delete("new"); setSearchParams(searchParams, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const save = async (addAnother = false) => {
    if (!form.name) return toast.error("Nama produk wajib diisi");
    const payload = {
      ...form,
      custom_step: Number(form.custom_step) || 0,
      discount_percent: Number(form.discount_percent) || 0,
      discount_amount: Number(form.discount_amount) || 0,
    };
    try {
      if (editing) { await api.put(`/products/${editing.id}`, payload); toast.success("Produk diperbarui"); setOpen(false); }
      else {
        await api.post("/products", payload);
        if (addAnother) {
          toast.success("Produk tersimpan — tambah lagi");
          // Keep trip, category & pricing defaults; clear item-specific fields
          setForm((f) => ({ ...EMPTY, trip_id: f.trip_id, category: f.category, availability: f.availability,
            margin_percent: f.margin_percent, jasa_titip_type: f.jasa_titip_type, jasa_titip_value: f.jasa_titip_value,
            delivery_fee: f.delivery_fee, rounding_method: f.rounding_method }));
          setAdvOpen(false);
        } else {
          toast.success("Produk ditambahkan"); setOpen(false);
        }
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };

  const doDelete = async () => {
    try { await api.delete(`/products/${delTarget.id}`); toast.success("Produk dihapus"); setDelTarget(null); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  const pricingValue = {
    aud_price: form.aud_price, margin_percent: form.margin_percent,
    jasa_titip_type: form.jasa_titip_type || "fixed", jasa_titip_value: form.jasa_titip_value,
    delivery_fee: form.delivery_fee, additional_fee: form.additional_fee, discount: form.discount,
    rounding_method: form.rounding_method || "none", custom_step: form.custom_step, quantity: 1,
  };
  const onPricingChange = (v) => setForm((f) => ({ ...f, aud_price: v.aud_price,
    margin_percent: v.margin_percent, jasa_titip_type: v.jasa_titip_type, jasa_titip_value: v.jasa_titip_value,
    delivery_fee: v.delivery_fee, additional_fee: v.additional_fee, discount: v.discount,
    rounding_method: v.rounding_method, custom_step: v.custom_step }));

  return (
    <div>
      <PageHeader title="Produk" subtitle="Katalog produk untuk trip">
        <Button variant="outline" onClick={() => setScreenshotOpen(true)} data-testid="product-screenshot-import-button"><Camera className="h-4 w-4 mr-1.5" />Impor Screenshot</Button>
        <Button onClick={openNew} data-testid="product-create-button"><Plus className="h-4 w-4 mr-1.5" />Tambah Produk</Button>
      </PageHeader>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="products-qty-summary">
          {[
            ["Total Produk", summary.total_products],
            ["Dipesan", summary.total_ordered],
            ["Dibeli", summary.total_purchased],
            ["Perlu dibeli", summary.total_required],
          ].map(([label, val]) => (
            <div key={label} className="rounded-xl border border-border bg-card p-3">
              <p className="text-[11px] text-muted-foreground">{label}</p>
              <p className="font-heading text-lg font-semibold">{val}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Cari produk, brand, toko..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="products-search-input" />
        </div>
        <Select value={availFilter || "all"} onValueChange={(v) => setAvailFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-40" data-testid="products-avail-filter"><SelectValue placeholder="Ketersediaan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Status</SelectItem>
            {(constants?.product_availability || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <SearchableSelect
          className="w-full sm:w-40"
          value={catFilter || "all"}
          onChange={(v) => setCatFilter(v === "all" ? "" : v)}
          options={[{ value: "all", label: "Semua Kategori", keywords: ["semua"] },
            ...(facets.categories || []).map((c) => ({ value: c, label: c }))]}
          placeholder="Kategori"
          searchPlaceholder="Cari kategori..."
          emptyText="Kategori tidak ditemukan"
          testId="products-category-filter"
        />
        <SearchableSelect
          className="w-full sm:w-40"
          value={brandFilter || "all"}
          onChange={(v) => setBrandFilter(v === "all" ? "" : v)}
          options={[{ value: "all", label: "Semua Brand", keywords: ["semua"] },
            ...(facets.brands || []).map((b) => ({ value: b, label: b }))]}
          placeholder="Brand"
          searchPlaceholder="Cari brand..."
          emptyText="Brand tidak ditemukan"
          testId="products-brand-filter"
        />
        <Select value={qtyFilter || "all"} onValueChange={(v) => setQtyFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-52" data-testid="products-qty-filter"><SelectValue placeholder="Status Pembelian" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Pembelian</SelectItem>
            <SelectItem value="SHORTAGE">Kurang Beli</SelectItem>
            <SelectItem value="SURPLUS">Surplus Stok</SelectItem>
            <SelectItem value="QUANTITY MATCHED">Sesuai</SelectItem>
            <SelectItem value="NOT PURCHASED">Belum Dibeli</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button onClick={() => setView("grid")} className={`px-3 h-10 ${view === "grid" ? "bg-primary text-primary-foreground" : "bg-card"}`} data-testid="products-grid-view"><LayoutGrid className="h-4 w-4" /></button>
          <button onClick={() => setView("list")} className={`px-3 h-10 ${view === "list" ? "bg-primary text-primary-foreground" : "bg-card"}`} data-testid="products-list-view"><List className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Bulk selection bar */}
      {!loading && items.length > 0 && (
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer select-none rounded-lg border border-border bg-card px-3 h-9" data-testid="products-select-all-label">
            <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="products-select-all" />
            <span className="text-sm font-medium">{allSelected ? "Batal pilih semua" : "Pilih semua"}</span>
          </label>
          {selectedIds.length > 0 && (
            <span className="text-sm text-muted-foreground" data-testid="products-selected-count">{selectedIds.length} dipilih</span>
          )}
        </div>
      )}

      {loading ? <SkeletonCardGrid /> : items.length === 0 ? (
        <EmptyState title="Belum ada produk" subtitle="Tambahkan produk yang dicari pelanggan." action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Tambah Produk</Button>} />
      ) : view === "grid" ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((p) => (
            <div key={p.id} className={`relative rounded-xl border bg-card overflow-hidden shadow-sm hover:shadow-md transition-shadow ${selectedIds.includes(p.id) ? "border-primary ring-2 ring-primary/40" : "border-border"}`} data-testid="product-card">
              <label className="absolute top-2 left-2 z-10 h-7 w-7 rounded-md bg-background/85 backdrop-blur border border-border flex items-center justify-center cursor-pointer shadow-sm" onClick={(e) => e.stopPropagation()}>
                <Checkbox checked={selectedIds.includes(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid="product-card-checkbox" aria-label={`Pilih ${p.name}`} />
              </label>
              <div className="aspect-square bg-secondary flex items-center justify-center overflow-hidden">
                {p.photo ? <img src={p.thumb_url || p.photo} alt={p.name} className="h-full w-full object-cover" /> : <ProductPlaceholder />}
              </div>
              <div className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-heading text-sm font-medium line-clamp-2">{p.name}</p>
                </div>
                {p.product_code && <p className="font-mono text-[10px] text-muted-foreground" data-testid="product-code">{p.product_code}</p>}
                {p.labels?.length > 0 && <LabelBadges labels={p.labels} />}
                <p className="text-xs text-muted-foreground">{p.brand} {p.variant && `· ${p.variant}`}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{formatAUD(p.aud_price)}</span>
                  <StatusBadge status={p.availability} />
                </div>
                {p.pricing?.discount_active && p.pricing?.discount_percent > 0 ? (
                  <div className="flex items-center gap-2 flex-wrap" data-testid="product-discount-preview">
                    <p className="font-heading font-semibold text-primary">{formatIDR(p.pricing?.final_price)}</p>
                    <span className="text-xs text-muted-foreground line-through">{formatIDR(p.pricing?.original_price)}</span>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]" data-testid="product-discount-badge">-{p.pricing?.discount_percent}%</span>
                  </div>
                ) : (
                  <p className="font-heading font-semibold text-primary">{formatIDR(p.pricing?.final_price)}</p>
                )}
                <div className="rounded-lg bg-secondary/40 px-2 py-1.5 text-[11px] grid grid-cols-3 gap-1 text-center">
                  <div><p className="text-muted-foreground">Dipesan</p><p className="font-semibold">{p.quantities?.ordered_quantity}</p></div>
                  <div><p className="text-muted-foreground">Dibeli</p><p className="font-semibold">{p.quantities?.purchased_quantity}</p></div>
                  <div><p className="text-muted-foreground">Kurang</p><p className="font-semibold">{p.quantities?.remaining_to_purchase}</p></div>
                </div>
                <StatusBadge status={p.quantities?.quantity_status} className="w-full justify-center" />
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => openDetail(p)} data-testid="product-detail-button">Detail</Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => openEdit(p)} data-testid="product-edit-button"><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-8 text-destructive" onClick={() => setDelTarget(p)} data-testid="product-delete-button"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {items.map((p) => (
            <div key={p.id} className={`flex items-center gap-3 p-3 border-b border-border last:border-0 ${selectedIds.includes(p.id) ? "bg-primary/5" : ""}`} data-testid="product-row">
              <Checkbox checked={selectedIds.includes(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid="product-row-checkbox" aria-label={`Pilih ${p.name}`} />
              <div className="h-12 w-12 rounded-lg bg-secondary flex items-center justify-center overflow-hidden shrink-0">
                {p.photo ? <img src={p.thumb_url || p.photo} alt={p.name} className="h-full w-full object-cover" /> : <ProductPlaceholder />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.product_code && <span className="font-mono">{p.product_code} · </span>}{p.trip_name} · {formatAUD(p.aud_price)}</p>
                <p className="text-[11px] text-muted-foreground">Dipesan {p.quantities?.ordered_quantity} · Dibeli {p.quantities?.purchased_quantity} · Kurang {p.quantities?.remaining_to_purchase}</p>
              </div>
              <StatusBadge status={p.quantities?.quantity_status} />
              <p className="font-mono text-sm font-semibold w-28 text-right hidden sm:block">{formatIDR(p.pricing?.final_price)}</p>
              <Button size="sm" variant="outline" onClick={() => openDetail(p)}>Detail</Button>
              <Button size="sm" variant="outline" onClick={() => openEdit(p)}><Pencil className="h-3.5 w-3.5" /></Button>
              <Button size="sm" variant="outline" className="text-destructive" onClick={() => setDelTarget(p)}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
        </div>
      )}

      {/* Product form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2 flex-wrap">{editing ? "Edit Produk" : "Tambah Produk"}{editing?.product_code && <span className="font-mono text-xs font-normal px-2 py-0.5 rounded-md bg-secondary text-muted-foreground" data-testid="product-form-code">{editing.product_code}</span>}</DialogTitle></DialogHeader>
          {!editing && (
            <div className="rounded-xl border border-dashed border-border bg-secondary/40 p-3 space-y-2">
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Impor cepat dari link toko</span>
              </div>
              <div className="flex gap-2">
                <Input value={importUrl} onChange={(e) => setImportUrl(e.target.value)} placeholder="Tempel link produk (https://...)" data-testid="product-import-url-input" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doImport(); } }} />
                <Button type="button" onClick={doImport} disabled={importing} data-testid="product-import-url-button" className="shrink-0">
                  {importing ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Mengambil…</> : "Isi otomatis"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">AI mengisi nama, harga (AUD, dibulatkan ke atas), deskripsi &amp; foto. Selalu periksa sebelum menyimpan.</p>
            </div>
          )}
          <div className="space-y-4">
            <ImageUpload value={form.photo} onChange={(b) => set("photo", b)} testId="product-photo-upload" />
            {/* Essentials */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Trip (pratinjau harga)" hint="Produk tersedia untuk semua trip">
                <SearchableSelect
                  value={form.trip_id}
                  onChange={onTripChange}
                  options={trips.map((t) => ({ value: t.id, label: t.name, sublabel: `kurs ${Number(t.exchange_rate).toLocaleString("id-ID")}`, keywords: [t.name, t.code] }))}
                  placeholder="Pilih trip"
                  searchPlaceholder="Cari trip..."
                  emptyText="Trip tidak ditemukan"
                  testId="product-trip-select"
                />
              </Field>
              <Field label="Ketersediaan">
                <Select value={form.availability} onValueChange={(v) => set("availability", v)}>
                  <SelectTrigger data-testid="product-avail-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(constants?.product_availability || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Nama Produk" required><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" className={importMissing.includes("nama") ? "ring-2 ring-[hsl(var(--warning))]" : ""} /></Field>
              <Field label="Kategori"><CategoryCombobox value={form.category} categories={categories} onChange={(v) => set("category", v)} onCreate={createCategory} testId="product-category" /></Field>
              <Field label="Harga AUD" required><Input type="number" step="0.01" value={form.aud_price} onChange={(e) => set("aud_price", e.target.value)} data-testid="product-aud-input" className={importMissing.includes("harga") ? "ring-2 ring-[hsl(var(--warning))]" : ""} /></Field>
            </div>
            <Field label="Deskripsi" hint="Tampil di katalog untuk pelanggan"><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="product-description-input" className={importMissing.includes("deskripsi") ? "ring-2 ring-[hsl(var(--warning))]" : ""} /></Field>

            <Field label="Label / Tanda" hint="Tampil sebagai badge di katalog. 'Terlaris' tampil paling atas.">
              <div className="flex flex-wrap gap-2" data-testid="product-labels-selector">
                {(constants?.product_labels || []).map((l) => {
                  const active = (form.labels || []).includes(l);
                  return (
                    <button key={l} type="button" data-testid={`product-label-${l}`}
                      onClick={() => set("labels", active ? form.labels.filter((x) => x !== l) : [...(form.labels || []), l])}
                      className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-secondary/60"}`}>
                      {l}
                    </button>
                  );
                })}
              </div>
            </Field>

            <Field label="Diskon" hint="Tampil di katalog: harga coret + harga diskon + badge %">
              <div className="rounded-xl border border-border p-3 space-y-3" data-testid="product-discount-box">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <Switch checked={!!form.discount_active} onCheckedChange={(v) => set("discount_active", v)} data-testid="product-discount-active" />
                  <span className="text-sm font-medium">Aktifkan diskon</span>
                </label>
                {form.discount_active && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      {[["percent", "Persen (%)"], ["amount", "Nominal (Rp)"]].map(([t, lbl]) => (
                        <button key={t} type="button" data-testid={`product-discount-type-${t}`}
                          onClick={() => set("discount_type", t)}
                          className={`flex-1 h-9 rounded-lg text-xs font-medium border transition-colors ${form.discount_type === t ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-secondary/60"}`}>
                          {lbl}
                        </button>
                      ))}
                    </div>
                    {form.discount_type === "amount" ? (
                      <Input type="number" min="0" placeholder="Potongan dalam Rupiah, mis. 50000" value={form.discount_amount} onChange={(e) => set("discount_amount", e.target.value)} data-testid="product-discount-amount" />
                    ) : (
                      <Input type="number" min="0" max="100" placeholder="Diskon persen, mis. 20" value={form.discount_percent} onChange={(e) => set("discount_percent", e.target.value)} data-testid="product-discount-percent" />
                    )}
                  </div>
                )}
              </div>
            </Field>

            <Collapsible open={advOpen} onOpenChange={setAdvOpen} className="border-t border-border pt-1">
              <CollapsibleTrigger asChild>
                <button type="button" data-testid="product-form-advanced-toggle"
                  className="w-full flex items-center justify-between py-2 text-sm hover:text-primary transition-colors">
                  <span className="font-heading font-medium">Lanjutan <span className="text-xs text-muted-foreground font-normal">· opsional (brand, varian, harga rinci)</span></span>
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${advOpen ? "rotate-180" : ""}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Brand"><Input value={form.brand} onChange={(e) => set("brand", e.target.value)} /></Field>
                  <Field label="Nama Toko"><Input value={form.store_name} onChange={(e) => set("store_name", e.target.value)} /></Field>
                  <Field label="Varian"><Input value={form.variant} onChange={(e) => set("variant", e.target.value)} /></Field>
                  <Field label="Warna"><Input value={form.colour} onChange={(e) => set("colour", e.target.value)} /></Field>
                  <Field label="Ukuran"><Input value={form.size} onChange={(e) => set("size", e.target.value)} /></Field>
                  <Field label="Harga Diskon AUD"><Input type="number" step="0.01" value={form.aud_sale_price || ""} onChange={(e) => set("aud_sale_price", e.target.value)} /></Field>
                  <Field label="URL Produk"><Input value={form.product_url} onChange={(e) => set("product_url", e.target.value)} /></Field>
                  <Field label="Estimasi Berat (kg)" hint="Untuk perkiraan bagasi & invoice">
                    <div className="flex gap-2">
                      <Input value={form.estimated_weight} onChange={(e) => set("estimated_weight", e.target.value)} data-testid="product-weight-input" />
                      <Button type="button" variant="outline" size="icon" className="shrink-0" onClick={estimateWeight} disabled={weighing || !form.name} title="Estimasi berat dengan AI" data-testid="product-weight-ai-button">
                        {weighing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scale className="h-4 w-4" />}
                      </Button>
                    </div>
                  </Field>
                </div>
                <div className="border-t border-border pt-4">
                  <p className="font-heading font-medium mb-3">Kalkulator Harga</p>
                  <PricingCalculator value={pricingValue} onChange={onPricingChange} exchangeRate={rate} />
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
          <div className="sticky bottom-0 z-10 -mx-6 -mb-6 px-6 py-3 bg-background/92 backdrop-blur border-t border-border flex items-center justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            {!editing && <Button variant="secondary" onClick={() => save(true)} data-testid="product-form-save-add-another-button">Simpan &amp; tambah lagi</Button>}
            <Button onClick={() => save(false)} data-testid="product-save-button">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product detail with tabs */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          {detail && (<>
            <DialogHeader><DialogTitle className="flex items-center gap-2">{detail.product.name} <StatusBadge status={detail.product.quantities?.quantity_status} /></DialogTitle></DialogHeader>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3 text-center">
              {[["Pelanggan", detail.summary.total_customers], ["Dipesan", detail.summary.total_ordered], ["Dibeli", detail.summary.total_purchased], ["Sisa Beli", detail.summary.total_remaining], ["Terkirim", detail.summary.total_delivered]].map(([l, v]) => (
                <div key={l} className="rounded-lg bg-secondary/40 p-2"><p className="text-[11px] text-muted-foreground">{l}</p><p className="font-semibold">{v}</p></div>
              ))}
            </div>
            {detail.product.quantities?.messages?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">{detail.product.quantities.messages.map((m, i) => <span key={i} className="text-[11px] rounded-full bg-secondary/70 px-2 py-1">{m}</span>)}</div>
            )}
            <Tabs value={detailTab} onValueChange={setDetailTab}>
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="info" data-testid="pd-tab-info">Informasi</TabsTrigger>
                <TabsTrigger value="orders" data-testid="pd-tab-orders">Order ({detail.orders.length})</TabsTrigger>
                <TabsTrigger value="purchases" data-testid="pd-tab-purchases">Pembelian ({detail.purchases.length})</TabsTrigger>
                <TabsTrigger value="deliveries" data-testid="pd-tab-deliveries">Pengiriman ({detail.deliveries.length})</TabsTrigger>
                <TabsTrigger value="audit" data-testid="pd-tab-audit">Riwayat</TabsTrigger>
              </TabsList>
              <TabsContent value="info">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Brand:</span> {detail.product.brand || "-"}</div>
                  <div><span className="text-muted-foreground">Kategori:</span> {detail.product.category || "-"}</div>
                  <div><span className="text-muted-foreground">Toko:</span> {detail.product.store_name || "-"}</div>
                  <div><span className="text-muted-foreground">Harga AUD:</span> {formatAUD(detail.product.aud_price)}</div>
                  <div><span className="text-muted-foreground">Harga jual:</span> {formatIDR(detail.product.pricing?.final_price)}</div>
                  <div><span className="text-muted-foreground">Ketersediaan:</span> {detail.product.availability}</div>
                </div>
              </TabsContent>
              <TabsContent value="orders">
                {detail.orders.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Belum ada order</p> :
                  detail.orders.map((o, i) => (
                    <div key={i} className="border-b border-border py-2 text-sm flex items-center justify-between gap-2 flex-wrap">
                      <div><p className="font-medium">{o.order_number} · {o.customer_name}</p><p className="text-xs text-muted-foreground">Qty {o.quantity} {o.variant && `· ${o.variant}`} · {formatIDR(o.selling_price)}</p></div>
                      <div className="flex gap-1"><StatusBadge status={o.order_status} /><StatusBadge status={o.payment_status} /></div>
                    </div>
                  ))}
              </TabsContent>
              <TabsContent value="purchases">
                {detail.purchases.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Belum ada pembelian</p> :
                  detail.purchases.map((p) => (
                    <div key={p.id} className="border-b border-border py-2 text-sm flex justify-between"><span>{p.store} · Qty {p.quantity}</span><span className="font-mono">{formatIDR(p.actual_total_idr)}</span></div>
                  ))}
              </TabsContent>
              <TabsContent value="deliveries">
                {detail.deliveries.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Belum ada pengiriman</p> :
                  detail.deliveries.map((d) => (
                    <div key={d.id} className="border-b border-border py-2 text-sm flex justify-between items-center"><span>{d.order_number} · {d.method} {d.courier}</span><StatusBadge status={d.status} /></div>
                  ))}
              </TabsContent>
              <TabsContent value="audit">
                {detail.audit.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Belum ada riwayat</p> :
                  detail.audit.map((a) => (
                    <div key={a.id} className="border-b border-border py-2 text-xs flex justify-between"><span>{a.action} · {a.user_name}</span><span className="text-muted-foreground">{formatDate(a.created_at)}</span></div>
                  ))}
              </TabsContent>
            </Tabs>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!delTarget} onOpenChange={() => setDelTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Hapus produk?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{delTarget?.name} akan dihapus (arsip).</p>
          <DialogFooter><Button variant="outline" onClick={() => setDelTarget(null)}>Batal</Button><Button variant="destructive" onClick={doDelete} data-testid="product-confirm-delete">Hapus</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sticky bulk action toolbar */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-md" data-testid="bulk-toolbar">
          <div className="rounded-2xl border border-border bg-card/95 backdrop-blur shadow-lg px-4 py-3 flex items-center gap-3">
            <span className="text-sm font-medium whitespace-nowrap">{selectedIds.length} dipilih</span>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={clearSelection} data-testid="bulk-clear-button">Batal</Button>
              <Button size="sm" onClick={() => { setBulkOp("discount_percent"); setBulkValue(""); setBulkOpen(true); }} data-testid="bulk-edit-button"><CheckSquare className="h-4 w-4 mr-1.5" />Edit Massal</Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk edit dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit Massal · {selectedIds.length} produk</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Field label="Aksi">
              <Select value={bulkOp} onValueChange={(v) => { setBulkOp(v); setBulkValue(v === "add_label" || v === "remove_label" ? "Terlaris" : (v === "availability" ? "Tersedia" : "")); }}>
                <SelectTrigger data-testid="bulk-op-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="discount_percent">Set diskon persen (%)</SelectItem>
                  <SelectItem value="discount_amount">Set diskon nominal (Rp)</SelectItem>
                  <SelectItem value="discount_enable">Aktifkan diskon</SelectItem>
                  <SelectItem value="discount_disable">Nonaktifkan diskon</SelectItem>
                  <SelectItem value="add_label">Tambah label (mis. Terlaris)</SelectItem>
                  <SelectItem value="remove_label">Hapus label</SelectItem>
                  <SelectItem value="category">Ubah kategori</SelectItem>
                  <SelectItem value="availability">Ubah ketersediaan</SelectItem>
                  <SelectItem value="archive">Arsipkan produk</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {bulkOp === "discount_percent" && (
              <Field label="Diskon (%)" hint="Nilai 0–100"><Input type="number" min="0" max="100" placeholder="mis. 20" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} data-testid="bulk-discount-percent-input" /></Field>
            )}
            {bulkOp === "discount_amount" && (
              <Field label="Potongan (Rp)"><Input type="number" min="0" placeholder="mis. 50000" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} data-testid="bulk-discount-amount-input" /></Field>
            )}
            {(bulkOp === "add_label" || bulkOp === "remove_label") && (
              <Field label="Label">
                <Select value={bulkValue || "Terlaris"} onValueChange={setBulkValue}>
                  <SelectTrigger data-testid="bulk-label-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(constants?.product_labels || []).map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            {bulkOp === "category" && (
              <Field label="Kategori"><CategoryCombobox value={bulkValue} categories={categories} onChange={setBulkValue} onCreate={createCategory} testId="bulk-category" /></Field>
            )}
            {bulkOp === "availability" && (
              <Field label="Ketersediaan">
                <Select value={bulkValue || "Tersedia"} onValueChange={setBulkValue}>
                  <SelectTrigger data-testid="bulk-availability-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{(constants?.product_availability || []).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            {bulkOp === "discount_enable" && <p className="text-sm text-muted-foreground">Mengaktifkan diskon pada produk terpilih (memakai nilai diskon yang sudah tersimpan).</p>}
            {bulkOp === "discount_disable" && <p className="text-sm text-muted-foreground">Menonaktifkan diskon pada produk terpilih.</p>}
            {bulkOp === "archive" && <p className="text-sm text-[hsl(var(--danger))]">Produk yang diarsipkan tidak lagi tampil di katalog maupun daftar produk.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy}>Batal</Button>
            {bulkOp === "archive" ? (
              <Button variant="destructive" onClick={() => runBulk("archive", null)} disabled={bulkBusy} data-testid="bulk-apply-button">
                {bulkBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Archive className="h-4 w-4 mr-1.5" />}Arsipkan
              </Button>
            ) : (
              <Button onClick={applyBulkEdit} disabled={bulkBusy} data-testid="bulk-apply-button">
                {bulkBusy ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Menyimpan…</> : "Terapkan"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScreenshotImportDialog
        open={screenshotOpen}
        onOpenChange={setScreenshotOpen}
        trips={trips}
        selectedTripId={selectedTripId}
        categories={categories}
        onCreate={createCategory}
        onCreated={() => { load(); loadCategories(); }}
      />
    </div>
  );
}
